import { useContext } from "react";
import UserContext from "../../contexts/userContext";

import Modal from "react-bootstrap/Modal";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

export const HelpModal = ({showHelpSidebar, setShowHelpSidebar}) => {
    const { userContext } = useContext(UserContext);
    const defaultActiveKey = userContext.tour ? 'faq' : 'tips';

    return(
        <Modal 
            show={showHelpSidebar}
            className='text-muted'
            id='helpModal'
            onHide={() => setShowHelpSidebar(false)}
        >
            <Modal.Header className="py-1" closeButton >
                <i className='bi-question-circle px-3' style={{"fontSize":"1.5em"}} />
                <Modal.Title >Help</Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <Accordion defaultActiveKey={defaultActiveKey}>
                    <Accordion.Item eventKey='docs' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-file-text pe-2' />
                            Bodies of Knowledge
                        </Accordion.Header>
                        <Accordion.Body>
                            <p>Before having a conversation with the chatbot, you must first select a <i>body of knowledge</i> for the chatbot to use when interacting with you.</p>
                            <p>You can interact with the chatbot using only its general knowledge, or you can select a domain of information that is proprietary to Analytics8.</p>
                            <p>This gives the chatbot additional context and knowledge beyond the general knowledge on which it was trained.
                                Documents - along with a Query Context - are
                                powerful ways to give the chatbot additional knowledge in order to provide very specific
                                answers based upon specific pieces of information.</p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey='convos' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-journal-text pe-2' />
                            Conversations
                        </Accordion.Header>
                        <Accordion.Body>
                            <p>Conversations with a chatbot are similar to conversations you have with another human -- a give-and-take of information.</p>
                            <p>Conversations with a chatbot are most productive when focused on a particular subject. While humans are able
                            to juggle multiple subjects within a single interaction, a chatbot assumes that all prior information within a conversation
                            is relevant to your most recent question. In other words, you may confuse the chatbot when mixing topics within a single conversation.</p>
                            <p><b><i>This is why you should start a new conversation when asking the chatbot about another topic.</i></b></p>
                            <p>But don't worry - you can pick a conversation back up at any time! While an LLM itself doesn't have 
                            memory, this chatbot app "remembers" everything within a conversation and "reminds" the LLM about everything
                            previously discussed when re-entering a conversation.</p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey='qc' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-puzzle pe-2' />
                            Query Context
                        </Accordion.Header>
                        <Accordion.Body>
                            <p>Query Context is additional knowledge that you give to the chatbot at the moment you ask a question or give a command so it can provide better answers.  You can add a Query Context by tapping the <i className='bi-puzzle-fill' /> icon on the left side of the input bar.</p>
                            <p>This a powerful way to augment the chatbot's pre-existing general knowledge with information it would not otherwise have.
                                </p>
                            <p>While you can always provide context within your question to the chatbot, <b>using Query Context is a convenient way to 
                                organize your thoughts - and the thoughts of the chatbot - by keeping your questions and commands separate from context</b>. This approach
                                can make the chatbot perform better, and it keeps your message history more compact and tidy.
                            </p>
                            <p><b>A Query Context is specific to a single question or command</b>, but since the chatbot has memory of all messages and context within a conversation, it's 
                                usually unnecessary to provide the same Query Context again and again within a conversation.</p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey='pt' className="text-muted" style={{"display":"none"}}>
                        <Accordion.Header>
                            <i className='bi-question-square pe-2' />
                            Prompt Templates
                        </Accordion.Header>
                        <Accordion.Body>
                            <p>Prompt Templates are used by the chatbot to nudge the LLM to generate the best possible answer for typical use cases.</p>
                            <p>Prompt Templates are used to convert your human input to into a more complex question that makes the 
                                LLM perform better. While this happens transparently, you can tap the <i className="bi-info-circle-fill" /> icon
                                in the top-right of your message to see what was used behind the scenes by the LLM.</p>
                            <p>While the default Prompt Template works well in most cases, you can select and tweak other Prompt Templates by
                                tapping the <i className="bi-question-square-fill" /> icon. These templates are optimized to
                                make the LLM perform well for specific use cases, and they can help you think of new creative 
                                ways to use this chatbot!</p>
                            <p>Some Prompt Templates require a Query Context. In this situation, a
                                message will appear on the bottom-left of the Prompt Template pop-up, and the Query Context
                                icon <i className='bi-puzzle-fill danger-color' /> turns red. </p>
                            <p>Some Prompt Templates may also set a default query that will appear in the query input. You can tweak
                                this default query if needed.
                            </p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey='tips' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-lightbulb pe-2' />
                            Tips and Tricks
                        </Accordion.Header>
                        <Accordion.Body>
                            <ul>
                                <li>
                                    <b>Don't use this chatbot as a Google substitute.</b> While Google is superior for fact-finding missions 
                                    based upon up-to-date public data, this chatbot is superior at summarizing, proofreading, translating, and creating
                                    new content. 
                                </li>
                                <li>
                                    <b>Assume the chatbot is <u>not</u> an expert on your question.</b> It was trained on vast amounts of data,
                                    but its training is finite and dated. Give as much context as possible - in your question or command, by using a body of knowledge with access to proprietary information,
                                    and/or via Query Context - so the chatbot has enough knowledge to generate an acceptable answer.
                                </li>
                                <li>
                                    <b>Try again!</b> If the chatbot doesn't answer your question sufficiently, simply tell it to try again! (You can type 
                                    this command or tap the <i className="bi-repeat" /> icon in the chatbot's response message.) If still insufficient,
                                    add more context, be more specific, and try again!
                                </li>
                                <li><b>Don't mix topics within a conversation.</b> This chatbot assumes all information previously exchanged in
                                    a conversation is relevant to any new questions you ask, so it can get confused if you mix too many topics.</li>
                                <li>
                                    <b>Be specific.</b> The more specific the better. Think of the chatbot as a new hire into your organization. You wouldn't expect a new hire to magically know
                                     exactly what you want based upon a vague question or directive, and the same should be true with this chatbot.
                                     Give very, very specific directions.
                                </li>
                                <li>
                                    <b>Make the chatbot assume a persona.</b> For example, if you need help reviewing or modifying a contract,
                                    tell the chatbot, "You are a contract attorney for a professional services organization."
                                    If you need help reviewing or creating a blog post, tell the chatbot, "You are a copywriter with a specialty in B2B marketing."
                                </li>
                                <li><b>Demand good formatting.</b> For example, if you ask a question about code and the chatbot does not format its answer
                                    well, tell it to, "format code inside of a code block." If you want a bulleted or numbered list in your response,
                                    tell the chatbot that's what you expect.</li>
                                <li style={{"display":"none"}}><b>Use Prompt Templates.</b> They were developed and tested to get the most of the LLM, and 
                                    they can help you think of new ways to use this chatbot that you haven't considered!</li>
                                <li><b>Use Query Contexts.</b> They help you and the chatbot organize thoughts to perform better.</li>
                                <li style={{"display":"none"}}><b>Resize question input box.</b> The question box can be resized to accommodate very long questions, and it
                                    automatically grows if you press enter in the middle of your question. It will also grow if you press enter at the 
                                    end of your question if this option is switched off:&nbsp;&nbsp;
                                        <div style={{'display':'inline-block'}}>
                                            <InputGroup.Text >
                                                <i className={'bi-lightning text-muted'} />
                                                <Form.Check type={'switch'} defaultChecked={true} />
                                            </InputGroup.Text>
                                        </div>
                                </li>
                                <li><b>Use this powerful tool as much as possible!</b> Make it part of your normal day-to-day workflows. Consistent use 
                                    will make you more comfortable using it, help you think of new use cases, and ultimately be more 
                                    productive. Your usage will become very natural - and likely indispensible - within very short period of time.</li>
                            </ul>
                        </Accordion.Body>
                    </Accordion.Item>
                    
                    <Accordion.Item eventKey='faq' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-patch-question pe-2' />
                            Frequently Asked Questions
                        </Accordion.Header>
                        <Accordion.Body>
                            <Card>
                                <Card.Header style={{"height":"2.5em"}} className="a8-blue-background">
                                    <i>Why should I use this chatbot rather than something like ChatGPT?</i>
                                </Card.Header>
                                <Card.Body>
                                    <p>This is a private chatbot interacting with a private large language model (LLM), self-hosted completely within 
                                    the Analytics8's private environment.</p>
                                    <p>All information exchanged with the chatbot stays within company resources and remains 
                                        our property. There is no risk of leakage of sensitive information to outsiders using the same public shared service, and
                                        our data won't be used to train somebody else's LLM.</p>
                                    <p>This chatbot can also have direct access to corporate assets to inform its answers - 
                                    something a public chatbot or LLM cannot do.</p>
                                </Card.Body>
                            </Card>

                            <Card>
                                <Card.Header className="a8-blue-background">
                                    <i>If I ask the chatbot something sensitive or give it sensitive context, will this information
                                    be used to train the LLM?<br />
                                    Can this information "leak" to co-workers if they ask a question
                                    that could be answered with information that I provide?</i>
                                </Card.Header>
                                <Card.Body>
                                    <p>No. Information you provide is isolated to <i>your</i> specific conversation within a specific document.</p>
                                </Card.Body>
                            </Card>

                            <Card>
                                <Card.Header style={{"height":"2.5em"}} className="a8-blue-background">
                                    <i>What is the difference between a chatbot and an LLM?</i>
                                </Card.Header>
                                <Card.Body>
                                    <p>An LLM (short for Large Language Model) is a type of AI that can understand,
                                        summarize, and generate content in natural language.</p>
                                    <p>A chatbot is an app that uses an LLM.</p>
                                    <p>An LLM by itself does not have memory, so it is unable to have a conversation.</p>
                                    <p>Additionally, an LLM by itself does not have access to additional context such as a document
                                        or other company intellectual property. An LLM needs to be part of an app like a chatbot 
                                        in order to provide answers about things outside of the limited dataset upon which it was trained.</p>
                                </Card.Body>
                            </Card>
                            
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey='changes' className="text-muted">
                        <Accordion.Header>
                            <i className='bi-tools pe-2' />
                            Change Log
                        </Accordion.Header>
                        <Accordion.Body>
                            <ul>
                                <li>8-Jan: Soft launch</li>
                                <li>9-Jan: Fixed re-authentication after session timeout</li>
                                <li>10-Jan: Support for deleting conversations, conversation summaries, prompt templates</li>
                                <li>11-Jan: Bug fix for new users, additional prompt templates</li>
                                <li>14-Jan: Added help menu</li>
                                <li>26-Jan: Improved experience for mobile devices</li>
                                <li>28-Jan: RAG support for ADM, AUP, and core messaging</li>
                            </ul>
                        </Accordion.Body>
                    </Accordion.Item>

                </Accordion>

            </Modal.Body>
        </Modal>
    )
}